import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { UserCog, Plus, KeyRound, Trash2, ShieldCheck, History } from "lucide-react";

const ROLES = [
  { v: "admin", l: "Master (admin)" },
  { v: "editor", l: "Editor" },
  { v: "viewer", l: "Viewer" },
];
const roleColor = { admin: "bg-[#1C2331] text-white", editor: "bg-[#065F46] text-white", viewer: "bg-slate-200 text-[#4B5563]" };

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [audit, setAudit] = useState([]);
  const [nu, setNu] = useState({ email: "", name: "", password: "", role: "editor" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/audit").then((r) => setAudit(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/users", nu);
      toast.success("Account created.");
      setNu({ email: "", name: "", password: "", role: "editor" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const changeRole = async (u, role) => {
    try { await api.put(`/users/${u.id}`, { name: u.name, role, active: u.active }); toast.success("Role updated."); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const toggleActive = async (u) => {
    try { await api.put(`/users/${u.id}`, { name: u.name, role: u.role, active: !u.active }); toast.success(u.active ? "Login disabled." : "Login enabled."); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const toggleAnalytics = async (u) => {
    try { await api.put(`/users/${u.id}`, { name: u.name, role: u.role, active: u.active, analytics_access: !u.analytics_access }); toast.success(!u.analytics_access ? "Demand Intel enabled." : "Demand Intel hidden."); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const resetPw = async (u) => {
    const pw = window.prompt(`Set a new password for ${u.email} (min 6 chars):`);
    if (!pw) return;
    try { await api.post(`/users/${u.id}/password`, { password: pw }); toast.success("Password reset."); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Account deleted."); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  return (
    <div className="space-y-8" data-testid="users-page">
      <div className="flex items-center gap-2">
        <UserCog className="w-6 h-6 text-[#D95D39]" />
        <h1 className="font-heading text-3xl font-bold text-[#0A0D14] tracking-tight">Users & Access</h1>
      </div>
      <p className="text-sm text-[#8CA3A7] -mt-4">Only you (master) can manage logins, roles and passwords. Editors can add/edit orders; Viewers can only view and download reports.</p>

      <div className="bg-white border border-[#E6E4DD] rounded-sm p-5">
        <div className="flex items-center gap-2 mb-4"><Plus className="w-4 h-4 text-[#065F46]" /><h2 className="font-heading font-bold text-[#1C2331]">Create login</h2></div>
        <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Email</label>
            <input data-testid="new-user-email" type="email" required value={nu.email} onChange={(e) => setNu({ ...nu, email: e.target.value })} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Name</label>
            <input data-testid="new-user-name" value={nu.name} onChange={(e) => setNu({ ...nu, name: e.target.value })} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Password</label>
            <input data-testid="new-user-password" required value={nu.password} onChange={(e) => setNu({ ...nu, password: e.target.value })} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Role</label>
            <select data-testid="new-user-role" value={nu.role} onChange={(e) => setNu({ ...nu, role: e.target.value })} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#1C2331]">
              {ROLES.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
            </select>
          </div>
          <button data-testid="create-user-btn" disabled={busy} className="md:col-span-5 md:w-auto bg-[#1C2331] text-white rounded-sm px-4 py-2.5 text-sm font-medium hover:bg-[#2A3447] disabled:opacity-60">{busy ? "Creating…" : "Create login"}</button>
        </form>
      </div>

      <div className="bg-white border border-[#E6E4DD] rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F6F5F2] text-[10px] uppercase tracking-wider text-[#8CA3A7]">
            <tr><th className="px-4 py-3 text-left">User</th><th className="px-4 py-3 text-left">Role</th><th className="px-4 py-3 text-left">Status</th><th className="px-4 py-3 text-left">Demand Intel</th><th className="px-4 py-3 text-right">Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-[#E6E4DD]">
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.email}`}>
                <td className="px-4 py-3">
                  <div className="font-medium text-[#1C2331]">{u.name || "—"}</div>
                  <div className="text-xs text-[#8CA3A7]">{u.email}</div>
                </td>
                <td className="px-4 py-3">
                  <select data-testid={`role-select-${u.email}`} value={u.role} onChange={(e) => changeRole(u, e.target.value)} disabled={u.id === user?.id} className={`text-xs rounded-sm px-2 py-1 font-medium ${roleColor[u.role]} disabled:opacity-70`}>
                    {ROLES.map((r) => <option key={r.v} value={r.v} className="bg-white text-[#1C2331]">{r.l}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <button data-testid={`toggle-active-${u.email}`} onClick={() => toggleActive(u)} disabled={u.id === user?.id} className={`text-xs px-2 py-1 rounded-sm ${u.active ? "bg-green-100 text-[#065F46]" : "bg-red-100 text-[#991B1B]"} disabled:opacity-60`}>{u.active ? "Active" : "Disabled"}</button>
                </td>
                <td className="px-4 py-3">
                  {u.role === "admin" ? (
                    <span className="text-xs px-2 py-1 rounded-sm bg-slate-100 text-[#4B5563]">Always</span>
                  ) : (
                    <button data-testid={`toggle-analytics-${u.email}`} onClick={() => toggleAnalytics(u)} className={`text-xs px-2 py-1 rounded-sm border transition-colors ${u.analytics_access ? "bg-[#FDE9E1] text-[#D95D39] border-[#F4C4B2]" : "bg-slate-100 text-[#8CA3A7] border-transparent hover:border-[#E6E4DD]"}`}>{u.analytics_access ? "Visible" : "Hidden"}</button>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    <button data-testid={`reset-pw-${u.email}`} onClick={() => resetPw(u)} title="Reset password" className="text-[#4B5563] hover:bg-slate-100 p-1.5 rounded-sm"><KeyRound className="w-4 h-4" /></button>
                    <button data-testid={`delete-user-${u.email}`} onClick={() => remove(u)} disabled={u.id === user?.id} className="text-[#991B1B] hover:bg-red-50 p-1.5 rounded-sm disabled:opacity-30"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-white border border-[#E6E4DD] rounded-sm p-5">
        <div className="flex items-center gap-2 mb-4"><History className="w-4 h-4 text-[#D95D39]" /><h2 className="font-heading font-bold text-[#1C2331]">Recent activity — who changed what</h2></div>
        <div className="space-y-1 max-h-96 overflow-auto" data-testid="audit-feed">
          {audit.length === 0 && <p className="text-sm text-[#8CA3A7]">No activity yet.</p>}
          {audit.map((a, i) => (
            <div key={i} className="flex items-center gap-3 text-xs font-mono py-1.5 border-b border-[#F0EEE9] last:border-0">
              <ShieldCheck className="w-3 h-3 text-[#8CA3A7] shrink-0" />
              <span className="text-[#1C2331] font-medium truncate max-w-[200px]">{a.user}</span>
              <span className="px-1.5 py-0.5 rounded-sm bg-slate-100 text-[#4B5563]">{a.action}</span>
              <span className="text-[#8CA3A7] truncate flex-1">{a.path}</span>
              <span className="text-[#8CA3A7] shrink-0">{(a.at || "").slice(0, 16).replace("T", " ")}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
